"use client";

import * as React from "react";
import type { ButtonHTMLAttributes, ComponentType } from "react";
import { MoreHorizontal } from "lucide-react";

import { cn } from "@/lib/utils";

export type IconButtonInteractionState =
  | "rest"
  | "hover"
  | "pressed"
  | "focus"
  | "disabled";

export type IconButtonProps = {
  /**
   * Accessible label for the icon-only button. Defaults to "Icon button" if not provided.
   */
  ariaLabel?: string;
  /**
   * Optional icon override. Defaults to a Lucide MoreHorizontal glyph.
   */
  icon?: ComponentType<{ className?: string }>;
  iconClassName?: string;
  /**
   * Force a visual state for documentation or screenshots. Falls back to real user interaction.
   */
  interactionState?: IconButtonInteractionState;
  /**
   * Marks the control as active/toggled. When provided, aria-pressed mirrors the value and the icon flips to the
   * primary color.
   */
  isActive?: boolean;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-label">;

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      ariaLabel,
      icon: Icon = MoreHorizontal,
      iconClassName,
      interactionState,
      isActive,
      className,
      type,
      disabled,
      children,
      ...buttonProps
    },
    ref
  ) => {
    const resolvedDisabled = disabled ?? interactionState === "disabled";
    const resolvedState: IconButtonInteractionState = resolvedDisabled
      ? "disabled"
      : interactionState ?? "rest";
    const label = ariaLabel ?? "Icon button";

    return (
      <button
        ref={ref}
        type={type ?? "button"}
        aria-label={label}
        aria-pressed={typeof isActive === "boolean" ? isActive : undefined}
        data-state={resolvedState}
        data-active={isActive ? "true" : undefined}
        disabled={resolvedDisabled}
        className={cn("icon-button", className)}
        {...buttonProps}
      >
        {children ?? <Icon aria-hidden className={cn("size-4", iconClassName)} />}
      </button>
    );
  }
);

IconButton.displayName = "IconButton";
